import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { snakeToCamelCase } from '@/utils/snakeCamelTransform';
import { getShipmentDetails } from '@/api/shipments';
import { useCompanyId } from '@/hooks/general/useCompanyId';
import { ShipmentItem } from '@/api/shipments/types';
import { queryClient } from '@/providers/QueryClientProvider';
import { GET_SHIPMENT_DETAILS_QUERY, UseShipmentDetailsQueryProps } from './types';

export const useGetShipmentDetailsQuery = ({
  shipmentId,
}: UseShipmentDetailsQueryProps): UseQueryResult<ShipmentItem, Error> => {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: [GET_SHIPMENT_DETAILS_QUERY, shipmentId],
    queryFn: async () => {
      return snakeToCamelCase(
        await getShipmentDetails({
          companyId,
          shipmentId: shipmentId as string,
        })
      );
    },
    enabled: !!shipmentId,
  });
};

export const useUpdateShipmentsDetailsQuery = ({ shipmentId }: { shipmentId?: string }) => {
  const queryClient = useQueryClient();
  return (newData: Partial<ShipmentItem>) => {
    const queryKey = [GET_SHIPMENT_DETAILS_QUERY, shipmentId];
    queryClient.setQueryData(queryKey, (item: ShipmentItem | undefined) => {
      if (!item) return item;
      return {
        ...item,
        ...newData,
      };
    });
  };
};

export const invalidateUseGetShipmentDetailsQuery = (shipmentId?: string) => {
  queryClient
    .invalidateQueries([GET_SHIPMENT_DETAILS_QUERY, shipmentId])
    .then()
    .catch(() => undefined);
};
